import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import Icon from 'components/Utils/NewIcon';
import DefaultButton from 'components/Utils/NewDefaultButton';
import moment from 'moment-timezone';
import { Portal } from '@material-ui/core';
import Draggable from 'react-draggable';
import { useAppSelector } from 'careand-redux/store';

/**
 * @param {any} props
 */
const DefaultModal = ({
  content,
  onRequestClose,
  isOpen,
  className = '',
  wrap = false,
  padding = true,
  onPin = undefined,
  pinStyle = {},
  title,
  style = {},
  outerStyle = {},
  headerContent,
  rightHeaderButtons,
  shouldCloseOnOverlayClick = false,
  scrollable = true,
  shouldCloseOnEsc = false,
  draggable = false,
}) => {
  let styleValue = getStyle(style, outerStyle);
  const modalRef = useRef(null);
  const isPatient = useAppSelector((state) => state.authentication.role) === 'patient';

  if (isPatient) {
    className = className + ' patient-modal';
  }

  useEffect(() => {
    const callback = (event) => {
      if (isOpen && event.key === 'Escape') {
        event.stopPropagation();
        onRequestClose();
      }
    };
    // const target = modalRef.current
    const target = document.body;
    const shouldAdd = shouldCloseOnEsc;
    const add = () => target.addEventListener('keydown', callback);
    const remove = () => target.removeEventListener('keydown', callback);

    if (shouldAdd) {
      add();
    } else {
      remove();
    }

    return () => {
      if (shouldAdd) {
        remove();
      } else {
        add();
      }
    };
  }, [shouldCloseOnEsc, onRequestClose, isOpen]);

  function closeOnOverlay(event) {
    if (!event?.target?.classList?.contains('default-modal-overlay')) {
      return;
    }
    if (event) event.stopPropagation();
    onRequestClose(event);
  }

  if (draggable && isOpen) {
    return (
      <DraggableModal onRequestClose={wrap ? undefined : onRequestClose} className={className}>
        <ModalContent
          wrap={wrap}
          title={title}
          style={style}
          onRequestClose={onRequestClose}
          padding={padding}
          content={content}
          headerContent={headerContent}
          scrollable={scrollable}
        />
      </DraggableModal>
    );
  }

  return (
    isOpen && (
      <Portal
        container={() =>
          document.querySelector('.staff-modal-container') || document.querySelector('body')
        }
      >
        <div
          className={'default-modal-overlay' + (isPatient ? ' patient-modal-overlay' : '')}
          onClick={shouldCloseOnOverlayClick ? closeOnOverlay : undefined}
        >
          <div className={'modal ' + className} style={styleValue} ref={modalRef}>
            {!!onPin && (
              <DefaultButton
                name="Pin to Side"
                icon="thumbtack"
                onClick={onPin}
                style={{ position: 'absolute', right: '70px', top: '8px', zIndex: 1, ...pinStyle }}
              />
            )}
            <ModalContent
              wrap={wrap}
              title={title}
              style={style}
              onRequestClose={onRequestClose}
              padding={padding}
              content={content}
              headerContent={headerContent}
              scrollable={scrollable}
              rightHeaderButtons={rightHeaderButtons}
            />
          </div>
        </div>
      </Portal>
    )
  );
};

const DraggableModal = ({ onRequestClose, className = '', children }) => {
  const [draggableKey] = useState(moment().unix());
  const uiOptions = useAppSelector((state) => state.ui.uiOptions);
  return (
    <Portal container={() => document.querySelector('.staff-modal-container')}>
      <Draggable key={draggableKey} bounds="body" handle=".handle">
        <div
          className={`draggable-modal ${onRequestClose ? 'allow-close' : ''} ${uiOptions.join(
            ' ',
          )} ${className}`}
        >
          <div className="draggable-top-bar">
            <Icon src="arrows-alt" width="20px" className="handle" />
            {onRequestClose && (
              <Icon src="xmark" width="20px" className="close-icon" onClick={onRequestClose} />
            )}
          </div>
          {children}
        </div>
      </Draggable>
    </Portal>
  );
};

const ModalContent = ({
  wrap,
  title,
  style,
  onRequestClose,
  padding,
  content,
  headerContent,
  scrollable,
  rightHeaderButtons,
}) =>
  wrap ? (
    <div
      className="modal modal-wrap animated fadeIn"
      id={title || 'default-modal'.replace(/ /g, '_') + '-modal'}
      style={style}
    >
      <div className="header">
        <div className="right-header">
          {rightHeaderButtons}
          {onRequestClose !== undefined && <Icon src="xmark" onClick={onRequestClose} width={20} />}
        </div>
        <div className="title">{headerContent || title}</div>
      </div>
      {scrollable && (
        <div className="content scroll-list">
          {padding ? <div className="padded-box">{content}</div> : content}
        </div>
      )}
      {!scrollable && (
        <div className="content">
          {padding ? <div className="padded-box">{content}</div> : content}
        </div>
      )}
    </div>
  ) : (
    content
  );

function getStyle(style, outerStyle) {
  return {
    overlay: { backgroundColor: 'rgba(38, 147, 209, 0.6)' },
    content: {
      background: 'none',
      border: 'none',
      position: 'relative',
      padding: 0,
      margin: 'auto',
      top: 0,
      left: 0,
      ...style,
    },
    ...outerStyle,
  };
}

export default DefaultModal;
